import { collection, doc, getDoc, getDocs, setDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../services/firebase';

/**
 * Get the currently active liability waiver
 */
export const getActiveWaiver = async () => {
  try {
    const waiversRef = collection(db, 'liabilityWaivers');
    const q = query(
      waiversRef,
      where('isActive', '==', true),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error fetching active waiver:', error);
    throw error;
  }
};

/**
 * Get user's waiver agreement status
 */
export const getUserWaiverAgreement = async (userId) => {
  try {
    const userWaiverRef = doc(db, 'userWaiverAgreements', userId);
    const docSnap = await getDoc(userWaiverRef);

    if (!docSnap.exists()) {
      return null;
    }

    return docSnap.data();
  } catch (error) {
    console.error('Error fetching user waiver agreement:', error);
    throw error;
  }
};

/**
 * Check if user needs to agree to current waiver
 * Returns { needsAgreement: boolean, waiver: object | null }
 */
export const checkWaiverCompliance = async (userId) => {
  try {
    const activeWaiver = await getActiveWaiver();

    // No active waiver - no agreement needed
    if (!activeWaiver) {
      return { needsAgreement: false, waiver: null };
    }

    const userAgreement = await getUserWaiverAgreement(userId);

    // User has never agreed to any waiver
    if (!userAgreement) {
      return { needsAgreement: true, waiver: activeWaiver };
    }

    // User's current version doesn't match active version
    if (userAgreement.currentVersion !== activeWaiver.version) {
      return {
        needsAgreement: true,
        waiver: { ...activeWaiver, isUpdate: true }
      };
    }

    // User is up to date
    return { needsAgreement: false, waiver: null };
  } catch (error) {
    console.error('Error checking waiver compliance:', error);
    throw error;
  }
};

/**
 * Get all waiver versions (for admin)
 */
export const getAllWaiverVersions = async () => {
  try {
    const waiversRef = collection(db, 'liabilityWaivers');
    const q = query(waiversRef, orderBy('effectiveDate', 'desc'));

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching all waivers:', error);
    throw error;
  }
};

/**
 * Create new waiver version (admin only)
 */
export const createWaiverVersion = async (waiverData) => {
  try {
    const { version, content, effectiveDate, createdBy } = waiverData;

    const waiverRef = doc(db, 'liabilityWaivers', version);

    await setDoc(waiverRef, {
      version,
      content,
      effectiveDate,
      isActive: false, // Must be manually activated
      createdAt: new Date().toISOString(),
      createdBy,
      updatedAt: new Date().toISOString()
    });

    return version;
  } catch (error) {
    console.error('Error creating waiver version:', error);
    throw error;
  }
};

/**
 * Activate waiver version (admin only)
 * Deactivates all other versions
 */
export const activateWaiverVersion = async (version, adminUserId) => {
  try {
    // First, deactivate all waivers
    const waiversRef = collection(db, 'liabilityWaivers');
    const snapshot = await getDocs(waiversRef);

    const updatePromises = snapshot.docs.map(async (docSnapshot) => {
      const waiverRef = doc(db, 'liabilityWaivers', docSnapshot.id);
      return setDoc(waiverRef, {
        isActive: docSnapshot.id === version,
        updatedAt: new Date().toISOString(),
        ...(docSnapshot.id === version && {
          activatedBy: adminUserId,
          activatedAt: new Date().toISOString()
        })
      }, { merge: true });
    });

    await Promise.all(updatePromises);

    console.log(`✅ Activated waiver version: ${version}`);
    return true;
  } catch (error) {
    console.error('Error activating waiver version:', error);
    throw error;
  }
};

/**
 * Get users who need to agree to current waiver (admin only)
 */
export const getUsersNeedingWaiverAgreement = async () => {
  try {
    const activeWaiver = await getActiveWaiver();
    if (!activeWaiver) return [];

    const agreementsRef = collection(db, 'userWaiverAgreements');
    // Fetch all agreements and filter in memory to avoid index requirement for != query
    const snapshot = await getDocs(agreementsRef);

    return snapshot.docs
      .map(doc => ({
        userId: doc.id,
        ...doc.data()
      }))
      .filter(agreement => agreement.currentVersion !== activeWaiver.version);

  } catch (error) {
    console.error('Error fetching users needing waiver:', error);
    throw error;
  }
};

/**
 * Get user's waiver agreement history (admin only)
 */
export const getUserWaiverHistory = async (userId) => {
  try {
    const userWaiverRef = doc(db, 'userWaiverAgreements', userId);
    const docSnap = await getDoc(userWaiverRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      userId,
      currentVersion: data.currentVersion,
      agreements: data.agreements || []
    };
  } catch (error) {
    console.error('Error fetching user waiver history:', error);
    throw error;
  }
};